/* prettier-ignore */

import { Injectable, inject } from '@angular/core';
import { DataChatService } from './dataChat.service';
import { AzurePrompt } from '../interface/azurePrompt.interface';
import { RequestService } from './request.service';
import { ScrollService } from './scrollContainer.service';
import { DebugService } from 'src/app/services/debug.service';
import { WebSocketService } from './webSocket.service';
import { v4 as uuidv4 } from 'uuid';

@Injectable({ providedIn: 'root' })
export class SendMessageService {
  private webSocketService = inject(WebSocketService);
  private debugService = inject(DebugService);

  public dataChat = inject(DataChatService);
  public request = inject(RequestService);
  public scrollService = inject(ScrollService);

  async sendMessageWebSocket(userInput: string, silent: boolean = false) {  
    this.webSocketService.sendWSChatBotData({ output_type: 'text', content: userInput });

    if (!silent) {
      this.dataChat.messages.update((prev) => [
        ...prev,
        {
          role: 'user',
          content: userInput,
          show: true,
        },
      ]);

      setTimeout(() => {
        this.scrollService.scrollToBottom();
      }, 0);
    }
  }

  async sendMessage(message: string = '', silent: boolean = false) {
    if (this.dataChat.isStreaming()) {
      this.dataChat.abortSignal().abort();
      this.dataChat.abortSignal.set(new AbortController());
      this.dataChat.isStreaming.set(false);
      this.dataChat.chatIsTyping.set(false);
      return;
    }

    this.dataChat.recommendedMessages.set([]);
    this.dataChat.toggleFormVisibility.set(false);
    this.dataChat.toggleSuccessMessage.set(false);
    this.dataChat.toggleErrorMessage.set(false);
    this.dataChat.canSaveChat.set(false);

    let userInput = message;
    if (message) {
      userInput = message;
      this.dataChat.recommendedMessages.set([]);
    }

    // OVERRIDE: Forzar WebSocket local en vez de Azure/Chatbase
    this.sendMessageWebSocket(userInput, silent);
    return;
  }

  // --- Legacy: Azure / Chatbase (deshabilitado) ---
  // if (this.dataChat.isAzure()) {
  //   this.sendToAzure(userInput);
  //   return;
  // }
  // this.sendToChatbase(userInput);

  sendToAzure(userInput: string) {
    this.debugService.info(this.dataChat.messagesForAzure(), '[SendMessageService] messagesForAzure');

    this.dataChat.messagesForAzure.update((prev) => [
      ...prev,
      { content: userInput, role: 'user' },
    ]);
    this.dataChat.messages.update((prev) => [
      ...prev,
      {
        role: 'user',
        content: userInput,
        show: true,
      },
    ]);
    setTimeout(() => {
      this.scrollService.scrollToBottom();
    }, 0);

    let question: AzurePrompt = {
      messages: this.dataChat.messagesForAzure(),
      chatbot_id: this.dataChat.id(),
      conversation_id: this.dataChat.token(),
      debug: this.dataChat.debug(),
      openai: this.dataChat.isOpenAi(),
      model3: this.dataChat.is35(),
    };

    this.request.getResponseFromAzure(question);
  }

  sendToChatbase(userInput: string) {
    this.dataChat.messagesForChatBase.update((prev) => [
      ...prev,
      { content: userInput, role: 'user' },
    ]);
    this.dataChat.messages.update((prev) => [
      ...prev,
      {
        role: 'user',
        content: userInput,
        show: true,
      },
    ]);

    setTimeout(() => {
      this.scrollService.scrollToBottom();
    }, 0);

    let question: any = {
      messages: this.dataChat.messagesForChatBase(),
      chatbotId: this.dataChat.chatbaseId(),
      stream: true,
      temperature: 0,
      model: 'gpt-4',
      conversationId: this.dataChat.token(),
    };

    this.request.getResponseFromChatbase(question);
  }

  sendImageMessage(file: File, prompt?: string) {
    const userContent = prompt ? `📷 [Imagen] ${prompt}` : '📷 [Imagen enviada]';

    // 1. Mostrar imagen en el chat (Optimistic UI)
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.dataChat.messages.update((prev) => [
        ...prev,
        {
          role: 'user',
          content: userContent,
          show: true,
          imageUrl: e.target.result // FIXED: Use imageUrl to match Message interface
        },
      ]);
      setTimeout(() => this.scrollService.scrollToBottom(), 100);
    };
    reader.readAsDataURL(file);

    // 2. Preparar FormData
    const completionId = uuidv4();
    this.webSocketService.trackCompletionId(completionId);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', this.dataChat.token());
    formData.append('service', this.dataChat.id());
    formData.append('completion_id', completionId);
    if (prompt) {
      formData.append('prompt', prompt);
    }

    this.dataChat.chatIsTyping.set(true);
    this.dataChat.isStreaming.set(true);

    // 3. Enviar al Backend
    this.request.sendImage(formData).subscribe({
      next: (event: any) => {
        // Aquí podríamos manejar el progreso si fuera necesario
        // La respuesta final llegará vía SSE/WebSocket igual que el texto
      },
      error: (err) => {
        this.debugService.error(err, 'Error uploading image');
        this.dataChat.chatIsTyping.set(false);
        this.dataChat.isStreaming.set(false);
        this.dataChat.messages.update((prev) => [
          ...prev,
          {
            content: 'Error al enviar la imagen. Inténtalo de nuevo.',
            role: 'assistant',
            show: true,
            danger: true,
          },
        ]);
      },
      complete: () => {
        // El worker procesará la imagen y responderá por el canal habitual
      }
    });
  }
}
